import { useState, useEffect, useCallback, useRef } from 'react';
import { useIntl } from 'react-intl';
import { Text, Button, Badge, Spinner, Card, Checkbox, Dropdown, Option } from '@fluentui/react-components';
import { ArrowLeftRegular, ArrowSyncRegular, ArrowUploadRegular, ErrorCircleRegular } from '@fluentui/react-icons';
import {
  listMigrateRuns, importMigrateRunFile, getRunDiff,
  getWorkloadCandidates, acceptWorkloadCandidates,
  type MigrateRun, type ImportOutcome, type MigrateRunDiff,
  type WorkloadCandidatesResult, type WorkloadAcceptOutcome,
} from '../services/migrate-service';
import { useApiToken } from '../hooks/useApiToken';
import { getBadgeProps, getConfidenceColor, MIGRATE_RUN_STATUS_BADGE } from '../utils/badge-config';
import { commitmentPricingGate } from '../utils/migrate-milestones';
import type { Project } from '../services/project-service';

const ACCEPTED_EXTENSIONS = '.xlsx,.zip,.json';
const MAX_UPLOAD_BYTES = 50 * 1024 * 1024; // mirror of MigrateRunsController.MaxUploadBytes
// Workload types the catalogue offers — mirror of WorkloadCatalogueLogic.AllowedTypes.
const WORKLOAD_TYPES = ['application-vm', 'database', 'cloud-pc', 'cloud-pc-gpu', 'storage', 'networking'];

interface MigratePageProps {
  project: Project;
  onBack: () => void;
}

/**
 * Azure Migrate assessment runs for the selected project (orchestrator RFC §4.7):
 * newest-first run history plus the browser-upload half of the
 * architect-workstation execution path (drag-drop or click to import).
 */
export function MigratePage({ project, onBack }: MigratePageProps) {
  const intl = useIntl();
  const getToken = useApiToken();
  const fileInputRef = useRef<HTMLInputElement>(null);

  const [runs, setRuns] = useState<MigrateRun[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [importing, setImporting] = useState(false);
  const [dragActive, setDragActive] = useState(false);
  const [outcome, setOutcome] = useState<ImportOutcome | null>(null);
  const [importError, setImportError] = useState<string | null>(null);

  const loadRuns = useCallback(async () => {
    if (!project.id) return;
    setLoading(true);
    setError(null);
    try {
      const token = await getToken();
      setRuns(await listMigrateRuns(project.id, project.customerId, token));
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load assessment runs.');
    } finally {
      setLoading(false);
    }
  }, [project.id, project.customerId, getToken]);

  useEffect(() => { loadRuns(); }, [loadRuns]);

  const importFile = useCallback(async (file: File) => {
    if (!project.id || importing) return;
    // Kestrel rejects an oversize body with a body-less 413 before the controller's
    // friendly message can run — pre-check here so the user sees the actual limit.
    if (file.size > MAX_UPLOAD_BYTES) {
      setOutcome(null);
      setImportError(intl.formatMessage({ id: 'migrate.import.tooLarge' }, { size: Math.round(file.size / (1024 * 1024)) }));
      return;
    }
    setImporting(true);
    setImportError(null);
    setOutcome(null);
    try {
      const token = await getToken();
      const result = await importMigrateRunFile(project.id, project.customerId, file, token);
      setOutcome(result);
      await loadRuns();
    } catch (err) {
      setImportError(err instanceof Error ? err.message : 'Import failed.');
    } finally {
      setImporting(false);
    }
  }, [project.id, project.customerId, importing, getToken, loadRuns, intl]);

  const onDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setDragActive(false);
    const file = e.dataTransfer.files?.[0];
    if (file) importFile(file);
  };

  const [diffFor, setDiffFor] = useState<MigrateRun | null>(null);
  const [diff, setDiff] = useState<MigrateRunDiff | null>(null);
  const [diffLoading, setDiffLoading] = useState(false);
  const [diffError, setDiffError] = useState<string | null>(null);
  // Monotonic request sequence — a slow stale diff response (the ad-hoc path computes
  // server-side and is slower than the stored-artifact path) must not land under a
  // newer row's heading.
  const diffReqRef = useRef(0);

  // Workload-candidate panel (RFC §4.5) — mutually exclusive with the diff panel below the table.
  const [candidatesFor, setCandidatesFor] = useState<MigrateRun | null>(null);
  const [candidates, setCandidates] = useState<WorkloadCandidatesResult | null>(null);
  const [candidatesLoading, setCandidatesLoading] = useState(false);
  const [candidatesError, setCandidatesError] = useState<string | null>(null);
  const candidatesReqRef = useRef(0);

  const closeDiff = () => { diffReqRef.current++; setDiffFor(null); setDiff(null); setDiffError(null); setDiffLoading(false); };
  const closeCandidates = () => { candidatesReqRef.current++; setCandidatesFor(null); setCandidates(null); setCandidatesError(null); setCandidatesLoading(false); };

  // Only successfully-imported runs can anchor a comparison — a failed or still-reserved
  // attempt has no usable result artifact and would 404 every click.
  const importedBaseline = useCallback((run: MigrateRun): string | undefined => {
    const supersedes = runs.find(r => r.id === run.supersedesRunId);
    if (supersedes?.status === 'imported') return supersedes.id;
    return runs
      .filter(r => r.runNumber < run.runNumber && r.status === 'imported')
      .sort((a, b) => b.runNumber - a.runNumber)[0]?.id;
  }, [runs]);

  const openDiff = useCallback(async (run: MigrateRun) => {
    if (!project.id) return;
    if (diffFor?.id === run.id) { closeDiff(); return; }

    // Stored ingest-time diff when available; otherwise an ad-hoc comparison against the
    // newest imported predecessor. The oldest run has no baseline.
    const against = run.diffArtifactId ? undefined : importedBaseline(run);
    if (!run.diffArtifactId && !against) return;

    closeCandidates(); // diff and candidate panels are mutually exclusive
    const seq = ++diffReqRef.current;
    setDiffFor(run);
    setDiff(null);
    setDiffError(null);
    setDiffLoading(true);
    try {
      const token = await getToken();
      const result = await getRunDiff(project.id, run.id, project.customerId, token, against);
      if (seq !== diffReqRef.current) return;
      setDiff(result);
    } catch (err) {
      if (seq !== diffReqRef.current) return;
      setDiffError(err instanceof Error ? err.message : 'Failed to load run diff.');
    } finally {
      if (seq === diffReqRef.current) setDiffLoading(false);
    }
  }, [project.id, project.customerId, diffFor, importedBaseline, getToken]); // eslint-disable-line react-hooks/exhaustive-deps

  const loadCandidates = useCallback(async (run: MigrateRun) => {
    if (!project.id) return;
    const seq = ++candidatesReqRef.current;
    setCandidatesLoading(true);
    setCandidatesError(null);
    try {
      const token = await getToken();
      const result = await getWorkloadCandidates(project.id, run.id, project.customerId, token);
      if (seq !== candidatesReqRef.current) return;
      setCandidates(result);
    } catch (err) {
      if (seq !== candidatesReqRef.current) return;
      setCandidatesError(err instanceof Error ? err.message : 'Failed to load workload candidates.');
    } finally {
      if (seq === candidatesReqRef.current) setCandidatesLoading(false);
    }
  }, [project.id, project.customerId, getToken]);

  const openCandidates = useCallback((run: MigrateRun) => {
    if (candidatesFor?.id === run.id) { closeCandidates(); return; }
    closeDiff(); // diff and candidate panels are mutually exclusive
    setCandidatesFor(run);
    setCandidates(null);
    loadCandidates(run);
  }, [candidatesFor, loadCandidates]); // eslint-disable-line react-hooks/exhaustive-deps

  const gate = commitmentPricingGate(runs, project.migrateContext?.discoveryStartedAt, new Date());
  const gateMessage =
    gate.reason === 'no-anchor' ? intl.formatMessage({ id: 'migrate.gate.noAnchor' })
    : gate.reason === 'maturing' ? intl.formatMessage({ id: 'migrate.gate.maturing' }, { due: gate.pendingUntil!.toLocaleDateString() })
    : gate.reason === 'no-month-run' ? intl.formatMessage({ id: 'migrate.gate.noMonthRun' })
    : gate.reason === 'low-confidence' ? intl.formatMessage({ id: 'migrate.gate.lowConfidence' })
    : intl.formatMessage({ id: 'migrate.gate.met' }, { run: gate.qualifyingRunNumber });
  const gateColor = gate.eligible ? 'success' : gate.reason === 'low-confidence' ? 'warning' : 'informative';

  return (
    <div className="page-root page-root--scrollable migrate-page">
      <div className="page-title-bar">
        <Button appearance="subtle" icon={<ArrowLeftRegular />} onClick={onBack} title={intl.formatMessage({ id: 'migrate.backButton.title' })} />
        <ArrowSyncRegular className="icon-title text-brand" />
        <Text size={500} weight="semibold">{intl.formatMessage({ id: 'migrate.title' }, { name: project.name })}</Text>
        <div className="title-bar-actions">
          <Badge size="small" appearance="tint" color={gateColor}>{gateMessage}</Badge>
        </div>
      </div>

      {/* Card, not page-empty: the empty-state triad class carries flex: 1 and would
          permanently split the page height with the runs table; .page-root--scrollable
          .fui-Card is flex-shrink: 0 by the shared card fix. */}
      <Card
        className="clickable"
        role="button"
        tabIndex={0}
        onClick={() => fileInputRef.current?.click()}
        onKeyDown={e => { if (e.key === 'Enter' || e.key === ' ') fileInputRef.current?.click(); }}
        onDragOver={e => { e.preventDefault(); setDragActive(true); }}
        onDragLeave={() => setDragActive(false)}
        onDrop={onDrop}
      >
        {importing ? (
          <Spinner size="small" label={intl.formatMessage({ id: 'migrate.dropzone.busy' })} />
        ) : (
          <div className="flex-row flex-wrap">
            <ArrowUploadRegular className={dragActive ? 'text-brand' : undefined} />
            <Text weight="semibold" className={dragActive ? 'text-brand' : undefined}>
              {intl.formatMessage({ id: 'migrate.dropzone.title' })}
            </Text>
            <Text className="text-muted">{intl.formatMessage({ id: 'migrate.dropzone.hint' })}</Text>
          </div>
        )}
        <input
          ref={fileInputRef}
          type="file"
          accept={ACCEPTED_EXTENSIONS}
          hidden
          onChange={e => {
            const file = e.target.files?.[0];
            if (file) importFile(file);
            e.target.value = '';
          }}
        />
      </Card>

      {importError && (
        <div className="flex-row">
          <ErrorCircleRegular className="text-danger" />
          <Text className="text-danger">{importError}</Text>
        </div>
      )}
      {outcome && (
        <div className="flex-col">
          <Text className="text-success">
            {intl.formatMessage({ id: 'migrate.import.success' }, {
              runId: outcome.runId,
              machines: outcome.machineCount,
              created: outcome.serversCreated,
              updated: outcome.serversUpdated,
            })}
          </Text>
          {outcome.warnings.length > 0 && (
            <Text className="text-warning">
              {intl.formatMessage({ id: 'migrate.import.warnings' }, { count: outcome.warnings.length })}
              {' '}{outcome.warnings.slice(0, 3).join('; ')}{outcome.warnings.length > 3 ? '…' : ''}
            </Text>
          )}
        </div>
      )}

      {loading ? (
        <div className="page-loading"><Spinner size="large" label={intl.formatMessage({ id: 'migrate.loading' })} /></div>
      ) : error ? (
        <div className="page-error">
          <ErrorCircleRegular />
          <Text className="text-danger">{error}</Text>
          <Button appearance="primary" onClick={loadRuns}>{intl.formatMessage({ id: 'migrate.error.retry' })}</Button>
        </div>
      ) : runs.length === 0 ? (
        <div className="page-empty">
          <ArrowSyncRegular />
          <Text className="text-muted">{intl.formatMessage({ id: 'migrate.empty' })}</Text>
        </div>
      ) : (
        <>
          <div className="table-scroll-container">
            <table className="data-table migrate-table">
              <thead>
                <tr>
                  <th>{intl.formatMessage({ id: 'migrate.col.run' })}</th>
                  <th>{intl.formatMessage({ id: 'migrate.col.kind' })}</th>
                  <th>{intl.formatMessage({ id: 'migrate.col.status' })}</th>
                  <th>{intl.formatMessage({ id: 'migrate.col.machines' })}</th>
                  <th>{intl.formatMessage({ id: 'migrate.col.confidence' })}</th>
                  <th>{intl.formatMessage({ id: 'migrate.col.perfDays' })}</th>
                  <th>{intl.formatMessage({ id: 'migrate.col.monthly' })}</th>
                  <th>{intl.formatMessage({ id: 'migrate.col.source' })}</th>
                  <th>{intl.formatMessage({ id: 'migrate.col.created' })}</th>
                  <th>{intl.formatMessage({ id: 'migrate.col.actions' })}</th>
                </tr>
              </thead>
              <tbody>
                {runs.map(run => {
                  const hasBaseline = run.diffArtifactId != null || importedBaseline(run) != null;
                  return (
                    <tr
                      key={run.id}
                      className={hasBaseline ? 'clickable' : undefined}
                      title={hasBaseline ? intl.formatMessage({ id: 'migrate.diff.rowHint' }) : undefined}
                      onClick={() => { if (hasBaseline) openDiff(run); }}
                    >
                      <td>
                        <Text weight="semibold">#{run.runNumber}</Text>
                        <div className="text-muted register-cell-sub">{run.executionPath}</div>
                      </td>
                      <td>
                        {run.kind}
                        {run.strategyName && <div className="text-muted register-cell-sub">{run.strategyName}</div>}
                      </td>
                      <td><Badge size="small" {...getBadgeProps(MIGRATE_RUN_STATUS_BADGE, run.status)}>{run.status}</Badge></td>
                      <td>{run.machineCount}</td>
                      <td>{run.confidenceStars != null
                        ? <Badge size="small" color={getConfidenceColor(run.confidenceStars)}>{'★'.repeat(run.confidenceStars)}</Badge>
                        : <span className="text-muted">—</span>}
                      </td>
                      <td>{run.perfDataDays ?? '—'}</td>
                      <td>{run.costSummary?.paygMonthly != null
                        ? `${run.costSummary.currency ?? ''} ${run.costSummary.paygMonthly.toLocaleString('en-GB', { maximumFractionDigits: 0 })}`.trim()
                        : '—'}
                      </td>
                      <td className="text-muted register-cell-meta">{run.sourceFile?.fileName ?? '—'}</td>
                      <td className="text-muted">{run.createdAt ? new Date(run.createdAt).toLocaleDateString() : '—'}</td>
                      <td>
                        {run.status === 'imported' && (
                          <Button
                            size="small"
                            appearance="subtle"
                            title={intl.formatMessage({ id: 'migrate.workloads.buttonHint' })}
                            onClick={e => { e.stopPropagation(); openCandidates(run); }}
                          >
                            {intl.formatMessage({ id: 'migrate.workloads.button' })}
                          </Button>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          {diffFor && (
            <RunDiffPanel run={diffFor} diff={diff} loading={diffLoading} error={diffError} onClose={closeDiff} />
          )}

          {candidatesFor && (
            <WorkloadCandidatesPanel
              key={candidatesFor.id}
              project={project}
              run={candidatesFor}
              result={candidates}
              loading={candidatesLoading}
              error={candidatesError}
              onClose={closeCandidates}
              onReload={() => loadCandidates(candidatesFor)}
            />
          )}
        </>
      )}
    </div>
  );
}

function runNumberFromId(runId: string): string {
  const tail = runId.split('-').pop() ?? runId;
  return String(parseInt(tail, 10) || tail);
}

function RunDiffPanel({ run, diff, loading, error, onClose }: {
  run: MigrateRun;
  diff: MigrateRunDiff | null;
  loading: boolean;
  error: string | null;
  onClose: () => void;
}) {
  const intl = useIntl();
  const noChanges = diff != null
    && diff.sizeChanges.length === 0 && diff.readinessTransitions.length === 0
    && diff.costChanges.length === 0 && diff.serversAdded.length === 0
    && diff.serversMissing.length === 0 && diff.conflictsWithOverrides.length === 0;

  return (
    <div className="detail-panel">
      <div className="section-header">
        <Text size={400} weight="semibold">{intl.formatMessage({ id: 'migrate.diff.heading' }, { run: run.runNumber })}</Text>
        {diff && <Text className="text-muted">{intl.formatMessage({ id: 'migrate.diff.vs' }, { base: runNumberFromId(diff.baseRunId) })}</Text>}
        <Button appearance="subtle" size="small" onClick={onClose}>{intl.formatMessage({ id: 'migrate.diff.close' })}</Button>
      </div>
      <div className="detail-panel-content">
        {loading && <Spinner size="small" label={intl.formatMessage({ id: 'migrate.diff.loading' })} />}
        {error && <Text className="text-danger">{error}</Text>}
        {diff && (
          <>
            {diff.crossScope && (
              <Text className="text-warning">
                {intl.formatMessage({ id: 'migrate.diff.crossScope' }, { reason: diff.crossScopeReason ?? '' })}
              </Text>
            )}
            <div className="flex-row flex-wrap">
              <Badge size="small" appearance="tint" color={diff.summary.sizeChanges > 0 ? 'warning' : 'subtle'}>
                {intl.formatMessage({ id: 'migrate.diff.chip.sizes' }, { n: diff.summary.sizeChanges })}
              </Badge>
              <Badge size="small" appearance="tint" color={diff.summary.readinessTransitions > 0 ? 'warning' : 'subtle'}>
                {intl.formatMessage({ id: 'migrate.diff.chip.readiness' }, { n: diff.summary.readinessTransitions })}
              </Badge>
              <Badge size="small" appearance="tint" color={diff.summary.costChanges > 0 ? 'informative' : 'subtle'}>
                {intl.formatMessage({ id: 'migrate.diff.chip.costs' }, { n: diff.summary.costChanges })}
              </Badge>
              <Badge size="small" appearance="tint" color={diff.summary.serversAdded > 0 ? 'informative' : 'subtle'}>
                {intl.formatMessage({ id: 'migrate.diff.chip.added' }, { n: diff.summary.serversAdded })}
              </Badge>
              <Badge size="small" appearance="tint" color={diff.summary.serversMissing > 0 ? 'warning' : 'subtle'}>
                {intl.formatMessage({ id: 'migrate.diff.chip.missing' }, { n: diff.summary.serversMissing })}
              </Badge>
              <Badge size="small" appearance={diff.summary.conflictsWithOverrides > 0 ? 'filled' : 'tint'} color={diff.summary.conflictsWithOverrides > 0 ? 'danger' : 'subtle'}>
                {intl.formatMessage({ id: 'migrate.diff.chip.conflicts' }, { n: diff.summary.conflictsWithOverrides })}
              </Badge>
              {diff.summary.paygMonthlyDelta != null && (
                <Text className={diff.summary.paygMonthlyDelta > 0 ? 'text-warning' : 'text-success'}>
                  {intl.formatMessage({ id: 'migrate.diff.payg' }, {
                    delta: `${diff.summary.paygMonthlyDelta >= 0 ? '+' : '−'}${diff.summary.currency ?? ''} ${Math.abs(diff.summary.paygMonthlyDelta).toLocaleString('en-GB', { maximumFractionDigits: 0 })}`,
                    percent: diff.summary.paygMonthlyDeltaPercent != null ? ` (${diff.summary.paygMonthlyDeltaPercent > 0 ? '+' : ''}${diff.summary.paygMonthlyDeltaPercent}%)` : '',
                  })}
                </Text>
              )}
            </div>

            {noChanges && <Text className="text-muted">{intl.formatMessage({ id: 'migrate.diff.noChanges' })}</Text>}

            {diff.conflictsWithOverrides.length > 0 && (
              <DiffSection titleId="migrate.diff.section.conflicts">
                <table className="data-table data-table--nested">
                  <thead>
                    <tr>
                      <th>{intl.formatMessage({ id: 'migrate.diff.col.server' })}</th>
                      <th>{intl.formatMessage({ id: 'migrate.diff.col.workload' })}</th>
                      <th>{intl.formatMessage({ id: 'migrate.diff.col.property' })}</th>
                      <th>{intl.formatMessage({ id: 'migrate.diff.col.architect' })}</th>
                      <th>{intl.formatMessage({ id: 'migrate.diff.col.newValue' })}</th>
                      <th>{intl.formatMessage({ id: 'migrate.diff.col.baseline' })}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {diff.conflictsWithOverrides.map((c, i) => (
                      <tr key={`${c.serverKey}-${c.propertyPath}-${i}`}>
                        <td>{c.name}</td>
                        <td>{c.workloadName ?? c.workloadId}</td>
                        <td className="control-ref">{c.propertyPath}</td>
                        <td>{c.architectValue ?? '—'}</td>
                        <td>{c.newAssessmentValue ?? '—'}</td>
                        <td>{c.stale
                          ? <Badge size="small" color="warning">{intl.formatMessage({ id: 'migrate.diff.stale' })}</Badge>
                          : <span className="text-muted">{c.deviationBaselineValue ?? '—'}</span>}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </DiffSection>
            )}

            {diff.sizeChanges.length > 0 && (
              <DiffSection titleId="migrate.diff.section.sizes">
                <ChangeTable rows={diff.sizeChanges} />
              </DiffSection>
            )}

            {diff.readinessTransitions.length > 0 && (
              <DiffSection titleId="migrate.diff.section.readiness">
                <ChangeTable rows={diff.readinessTransitions} />
              </DiffSection>
            )}

            {diff.costChanges.length > 0 && (
              <DiffSection titleId="migrate.diff.section.costs">
                <table className="data-table data-table--nested">
                  <thead>
                    <tr>
                      <th>{intl.formatMessage({ id: 'migrate.diff.col.server' })}</th>
                      <th>{intl.formatMessage({ id: 'migrate.diff.col.before' })}</th>
                      <th>{intl.formatMessage({ id: 'migrate.diff.col.after' })}</th>
                      <th>{intl.formatMessage({ id: 'migrate.diff.col.delta' })}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {diff.costChanges.map(c => (
                      <tr key={c.serverKey}>
                        <td>{c.name}</td>
                        <td>{c.beforeMonthly.toFixed(2)}</td>
                        <td>{c.afterMonthly.toFixed(2)}</td>
                        <td className={c.afterMonthly > c.beforeMonthly ? 'text-warning' : 'text-success'}>
                          {c.deltaPercent != null ? `${c.deltaPercent > 0 ? '+' : ''}${c.deltaPercent}%` : intl.formatMessage({ id: 'migrate.diff.newSpend' })}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </DiffSection>
            )}

            {diff.serversAdded.length > 0 && (
              <Text>
                <span className="text-muted">{intl.formatMessage({ id: 'migrate.diff.section.added' })}: </span>
                {diff.serversAdded.map(s => s.name).join(', ')}
              </Text>
            )}
            {diff.serversMissing.length > 0 && (
              <Text>
                <span className="text-muted">{intl.formatMessage({ id: 'migrate.diff.section.missing' })}: </span>
                {diff.serversMissing.map(s => s.name).join(', ')}
              </Text>
            )}
          </>
        )}
      </div>
    </div>
  );
}

function DiffSection({ titleId, children }: { titleId: string; children: React.ReactNode }) {
  const intl = useIntl();
  return (
    <div className="flex-col">
      <Text weight="semibold">{intl.formatMessage({ id: titleId })}</Text>
      {children}
    </div>
  );
}

function ChangeTable({ rows }: { rows: { serverKey: string; name: string; before?: string | null; after?: string | null }[] }) {
  const intl = useIntl();
  return (
    <table className="data-table data-table--nested">
      <thead>
        <tr>
          <th>{intl.formatMessage({ id: 'migrate.diff.col.server' })}</th>
          <th>{intl.formatMessage({ id: 'migrate.diff.col.before' })}</th>
          <th>{intl.formatMessage({ id: 'migrate.diff.col.after' })}</th>
        </tr>
      </thead>
      <tbody>
        {rows.map(r => (
          <tr key={r.serverKey}>
            <td>{r.name}</td>
            <td className="text-muted">{r.before ?? '—'}</td>
            <td>{r.after ?? '—'}</td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

/**
 * Workload auto-generation review/accept panel (RFC §4.5) — one candidate per assessed
 * machine. Eligible candidates are pre-selected; already-catalogued machines and names that
 * already exist are disabled. Keyed by run id in the parent so it remounts (and resets accept
 * state) when the run changes; the parent owns the fetch, this owns selection + accept.
 */
function WorkloadCandidatesPanel({ project, run, result, loading, error, onClose, onReload }: {
  project: Project;
  run: MigrateRun;
  result: WorkloadCandidatesResult | null;
  loading: boolean;
  error: string | null;
  onClose: () => void;
  onReload: () => void;
}) {
  const intl = useIntl();
  const getToken = useApiToken();

  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [types, setTypes] = useState<Record<string, string>>({});
  const [accepting, setAccepting] = useState(false);
  const [acceptResult, setAcceptResult] = useState<WorkloadAcceptOutcome | null>(null);
  const [acceptError, setAcceptError] = useState<string | null>(null);

  // Pre-select every eligible candidate on load and after the post-accept reload. Type overrides
  // are pruned to candidates still present-and-eligible — NOT reset — so an architect's in-progress
  // type choices on rows they have not yet accepted survive the reload. acceptResult is deliberately
  // not cleared here so the success line survives the reload; the panel is keyed by run id in the
  // parent, so switching runs remounts it with fresh state.
  useEffect(() => {
    if (!result) return;
    const eligible = result.candidates.filter(c => !c.alreadyCatalogued && !c.nameCollision);
    setSelected(new Set(eligible.map(c => c.serverKey)));
    setTypes(prev => {
      const next: Record<string, string> = {};
      for (const c of eligible) if (prev[c.serverKey] !== undefined) next[c.serverKey] = prev[c.serverKey];
      return next;
    });
    setAcceptError(null);
  }, [result]);

  const toggle = (serverKey: string, checked: boolean) =>
    setSelected(prev => {
      const next = new Set(prev);
      if (checked) next.add(serverKey); else next.delete(serverKey);
      return next;
    });

  const accept = async () => {
    if (!project.id || selected.size === 0 || accepting) return;
    setAccepting(true);
    setAcceptError(null);
    try {
      const token = await getToken();
      const items = [...selected].map(serverKey => ({
        serverKey,
        type: types[serverKey] ?? result?.candidates.find(c => c.serverKey === serverKey)?.suggestedType,
      }));
      const outcome = await acceptWorkloadCandidates(project.id, run.id, project.customerId, items, token);
      setAcceptResult(outcome);
      onReload(); // re-fetch so accepted candidates flip to already-catalogued
    } catch (err) {
      setAcceptError(err instanceof Error ? err.message : 'Failed to create workloads.');
    } finally {
      setAccepting(false);
    }
  };

  const skipped = acceptResult ? acceptResult.skippedAlreadyCatalogued + acceptResult.skippedNameCollision : 0;

  return (
    <div className="detail-panel">
      <div className="section-header">
        <Text size={400} weight="semibold">{intl.formatMessage({ id: 'migrate.workloads.heading' }, { run: run.runNumber })}</Text>
        <Button appearance="subtle" size="small" onClick={onClose}>{intl.formatMessage({ id: 'migrate.workloads.close' })}</Button>
      </div>
      <div className="detail-panel-content">
        {loading && <Spinner size="small" label={intl.formatMessage({ id: 'migrate.workloads.loading' })} />}
        {error && <Text className="text-danger">{error}</Text>}
        {result && !loading && (
          result.candidates.length === 0 ? (
            <Text className="text-muted">{intl.formatMessage({ id: 'migrate.workloads.empty' })}</Text>
          ) : (
            <>
              <Text className="text-muted">{intl.formatMessage({ id: 'migrate.workloads.subtitle' })}</Text>
              <table className="data-table data-table--nested">
                <thead>
                  <tr>
                    <th>{intl.formatMessage({ id: 'migrate.workloads.col.include' })}</th>
                    <th>{intl.formatMessage({ id: 'migrate.workloads.col.server' })}</th>
                    <th>{intl.formatMessage({ id: 'migrate.workloads.col.type' })}</th>
                    <th>{intl.formatMessage({ id: 'migrate.workloads.col.target' })}</th>
                    <th>{intl.formatMessage({ id: 'migrate.workloads.col.monthly' })}</th>
                    <th>{intl.formatMessage({ id: 'migrate.workloads.col.readiness' })}</th>
                    <th>{intl.formatMessage({ id: 'migrate.workloads.col.confidence' })}</th>
                    <th>{intl.formatMessage({ id: 'migrate.workloads.col.status' })}</th>
                  </tr>
                </thead>
                <tbody>
                  {result.candidates.map(c => {
                    const disabled = c.alreadyCatalogued || c.nameCollision;
                    const typeValue = types[c.serverKey] ?? c.suggestedType;
                    return (
                      <tr key={c.serverKey} className={disabled ? 'text-muted' : undefined}>
                        <td>
                          <Checkbox
                            checked={selected.has(c.serverKey)}
                            disabled={disabled}
                            onChange={(_, d) => toggle(c.serverKey, d.checked === true)}
                          />
                        </td>
                        <td>{c.name}</td>
                        <td>
                          {disabled ? c.suggestedType : (
                            <Dropdown
                              size="small"
                              value={typeValue}
                              selectedOptions={[typeValue]}
                              onOptionSelect={(_, d) => setTypes(prev => ({ ...prev, [c.serverKey]: d.optionValue ?? c.suggestedType }))}
                            >
                              {WORKLOAD_TYPES.map(t => <Option key={t} value={t}>{t}</Option>)}
                            </Dropdown>
                          )}
                        </td>
                        <td>{c.targetAzureSku ?? '—'}</td>
                        <td>{c.estimatedMonthlyCost != null
                          ? `${c.currency} ${c.estimatedMonthlyCost.toLocaleString('en-GB', { maximumFractionDigits: 0 })}`
                          : '—'}
                        </td>
                        <td>{c.readiness ?? '—'}</td>
                        <td>{c.confidenceStars != null
                          ? <Badge size="small" color={getConfidenceColor(c.confidenceStars)}>{'★'.repeat(c.confidenceStars)}</Badge>
                          : <span className="text-muted">—</span>}
                        </td>
                        <td>
                          {c.alreadyCatalogued
                            ? <Badge size="small" appearance="tint" color="subtle">{intl.formatMessage({ id: 'migrate.workloads.status.catalogued' })}</Badge>
                            : c.nameCollision
                              ? <Badge size="small" appearance="tint" color="warning">{intl.formatMessage({ id: 'migrate.workloads.status.nameExists' })}</Badge>
                              : <span className="text-muted">—</span>}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>

              <div className="flex-row flex-wrap">
                <Button appearance="primary" size="small" disabled={selected.size === 0 || accepting} onClick={accept}>
                  {selected.size > 0
                    ? intl.formatMessage({ id: 'migrate.workloads.accept' }, { count: selected.size })
                    : intl.formatMessage({ id: 'migrate.workloads.acceptNone' })}
                </Button>
                {accepting && <Spinner size="small" label={intl.formatMessage({ id: 'migrate.workloads.accepting' })} />}
                {acceptError && <Text className="text-danger">{acceptError}</Text>}
                {acceptResult && (
                  <Text className="text-success">
                    {intl.formatMessage({ id: 'migrate.workloads.accepted' }, { created: acceptResult.created })}
                    {skipped > 0 ? ' ' + intl.formatMessage({ id: 'migrate.workloads.acceptedSkipped' }, { skipped }) : ''}
                  </Text>
                )}
                {acceptResult && acceptResult.failedLinks > 0 && (
                  <Text className="text-warning">
                    {intl.formatMessage({ id: 'migrate.workloads.failedLinks' }, { n: acceptResult.failedLinks })}
                  </Text>
                )}
              </div>
            </>
          )
        )}
      </div>
    </div>
  );
}
